import { isoDateTime, z } from "../zod.js";
import { Problem } from "../common/problem.js";
import { PlanningDate, PlanningMonday, PlanningQty } from "./schemas.js";

// Module 03 planning, P3-H-2: the MRP contract (spec 03 §2.9 to §2.11, §3.3,
// §3.3c, §3.8 and §5 rows 38 to 49; D-385, D-387, D-388, D-389, D-390,
// D-395). The explanation schema below validates `planned_orders.explanation`
// on write and on read (D-387 ruling 3): keys, ids and numbers only, never
// words, the labels rendered per reader beside it. Quantities on an order are
// three-decimal strings in the product's base unit; inside the explanation
// they carry up to six decimals. No MRP response carries money.

const Uuid = z.guid();
const Timestamp = isoDateTime();
const PageCursor = z.string().optional();
const PageLimit = z.coerce.number().int().min(1).max(200).optional();

/** A quantity inside the explanation: exact decimal text, at most six places. */
export const ExplanationQty = z
  .string()
  .regex(/^\d{1,15}(\.\d{1,6})?$/)
  .openapi({ example: "2259.182566", description: "Exact decimal, at most 6 places" });

/** A key the reader's locale renders (`exceeds_approved_mps`, `clamped_to_today`). */
const ExplanationKey = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);

// --- enums -------------------------------------------------------------------------

export const MrpRunTrigger = z.enum(["mps_approval", "manual", "transfer_projection", "cron"]);
export const MrpRunStatus = z.enum(["queued", "running", "succeeded", "failed"]);
/** `mrp_runs.error` (§2.9), a key the workbench renders. */
export const MrpRunError = z.enum([
  "FACILITY_CALENDAR_MISSING",
  "DEMAND_REFRESH_FAILED",
  "LEASE_EXPIRED",
  "SHUTDOWN",
  "PLANNED_ORDERS_CHANGED",
  "TRANSFER_FIXPOINT_NOT_REACHED",
  "COMPUTE_FAILED",
]);
export const PlannedOrderKind = z.enum(["manufacture", "purchase", "transfer"]);
/** P3-H-3 widens this response enum with `converted` (D-389 ruling 3). */
export const PlannedOrderStatus = z.enum(["proposed", "firmed", "converted", "cancelled"]);

/** The document a converted order became (spec 03 §2.10, D-389 rulings 3 to 6). */
export const PlannedOrderConvertedKind = z.enum([
  "work_order",
  "purchase_proposal",
  "transfer_order",
]);
export const ActionMessageKind = z.enum([
  "reschedule_in",
  "reschedule_out",
  "increase_qty",
  "decrease_qty",
  "cancel",
  "expedite",
]);
export const PlannedOrderCancelledKind = z.enum([
  "regenerated_away",
  "manual",
  "action_message_cancel",
]);
export const PegSourceKind = z.enum([
  "demand_entry",
  "planned_order",
  "mps_line",
  "work_order",
  "excess",
]);
export const PegRefKind = z.enum([
  "sales_order_line",
  "forecast_line",
  "safety_stock_policy",
  "transfer_planned_order",
  "mps_line",
  "planned_order",
  "work_order",
  "none",
]);

// --- the explanation (§2.10) -------------------------------------------------------

export const ExplanationScrapStep = z
  .object({
    kind: z.enum(["batch", "component", "assembly", "operation"]),
    /** The batch's `output_qty`, or the factor's percentage. */
    value: ExplanationQty,
    bom_line_id: Uuid.optional(),
    product_id: Uuid.optional(),
    routing_step_id: Uuid.optional(),
    step_no: z.number().int().optional(),
    before: ExplanationQty,
    after: ExplanationQty,
  })
  .strict()
  .openapi("ExplanationScrapStep");

export const ExplanationDemandItem = z
  .object({
    source_kind: z.enum(["demand_entry", "planned_order", "mps_line", "work_order"]),
    /** The pool entry, while its refresh is kept (§2.6). */
    demand_entry_id: Uuid.optional(),
    source_ref_kind: PegRefKind.exclude(["none"]),
    source_ref_id: Uuid,
    qty: ExplanationQty,
    need_date: PlanningDate,
    base_qty: ExplanationQty.optional(),
    scrap_steps: z.array(ExplanationScrapStep).optional(),
    /**
     * Never written from P3-I-1 (D-405 ruling 4): MRP explodes every open
     * work order on its own facts and no longer grosses module 04's plan by a
     * factor. Declared, optional, only so an explanation stored before P3-I-1
     * (a converted or cancelled order keeps its last one) still reads under
     * this strict schema.
     */
    wo_scrap_alignment: z
      .object({ work_order_id: Uuid, factor: ExplanationQty })
      .strict()
      .optional()
      .openapi({
        deprecated: true,
        description:
          "Never written since P3-I-1 (D-405). Present only on explanations stored before it.",
      }),
  })
  .strict()
  .openapi("ExplanationDemandItem");

export const ExplanationNetOfOnHand = z
  .object({
    remnants_matched: ExplanationQty,
    remnants: z.array(
      z
        .object({
          remnant_id: Uuid,
          remnant_no: z.string().min(1).max(64),
          qty: ExplanationQty,
          length_m: z.string().nullable(),
        })
        .strict(),
    ),
    on_hand: ExplanationQty,
    reserved: ExplanationQty,
    in_transit: ExplanationQty,
    open_supply: ExplanationQty,
    firmed: ExplanationQty,
    co_by_products: ExplanationQty,
    lot_excess_carried: ExplanationQty,
    offcuts_matched: ExplanationQty,
    net: ExplanationQty,
  })
  .strict()
  .openapi("ExplanationNetOfOnHand");

export const ExplanationMps = z
  .object({
    mps_line_id: Uuid,
    cell_qty: ExplanationQty,
    committed: ExplanationQty,
    overflow: ExplanationQty,
    excess: ExplanationQty,
    excess_sources: z.array(
      z
        .object({
          source_kind: z.enum(["demand_entry", "planned_order", "work_order"]),
          source_ref_kind: PegRefKind.exclude(["none"]),
          source_ref_id: Uuid,
          qty: ExplanationQty,
        })
        .strict(),
    ),
  })
  .strict()
  .openapi("ExplanationMps");

export const ExplanationOffsetKind = z.enum([
  "due_snap",
  "routing_time",
  "lead_time_days",
  "source_snap",
  "release_buffer",
  "version_repicked",
]);

export const ExplanationOffset = z
  .object({
    kind: ExplanationOffsetKind,
    days: z.number().int(),
    /** Keys, ids, dates and numbers of the offset (§2.10), never words. */
    data: z.record(z.string(), z.unknown()),
  })
  .strict()
  .openapi("ExplanationOffset");

export const ExplanationDateMath = z
  .object({
    due_date: PlanningDate,
    offsets: z.array(ExplanationOffset),
    start_date: PlanningDate,
    release_date: PlanningDate,
    skipped_dates: z.array(PlanningDate),
    exception_ids: z.array(Uuid),
    clamped: z.boolean(),
    late: z.boolean(),
    notes: z.array(ExplanationKey),
    original_start_date: PlanningDate.optional(),
    original_release_date: PlanningDate.optional(),
  })
  .strict()
  .openapi("ExplanationDateMath");

/** `planned_orders.explanation` (spec 03 §2.10, D-387 ruling 3). */
export const PlannedOrderExplanation = z
  .object({
    demand: z.array(ExplanationDemandItem),
    qty_math: z
      .object({
        gross_requirement: ExplanationQty,
        net_of_on_hand: ExplanationNetOfOnHand,
        lot_sizing: z
          .object({
            method: z.enum(["lot_for_lot", "min_multiple"]),
            min_lot_qty: z.string().nullable(),
            lot_multiple_qty: z.string().nullable(),
            before: ExplanationQty,
            after: ExplanationQty,
          })
          .strict(),
        mps: ExplanationMps.nullable(),
        notes: z.array(ExplanationKey),
      })
      .strict(),
    date_math: ExplanationDateMath,
  })
  .strict()
  .openapi("PlannedOrderExplanation");

// --- action messages (§2.10, §3.8) ---------------------------------------------------

/**
 * The message's fields, unregistered. The nullable uses below take this inline
 * copy rather than a reference: a nullable reference made the registered
 * component itself nullable, and the generated client then typed every request
 * body that names it as an intersection no value satisfies, so the web could
 * not send accept or dismiss (the saved-views schemas hit the same and took the
 * same way out).
 */
const ExpectedActionMessageFields = z
  .object({
    kind: ActionMessageKind,
    suggestion: z.string().max(32),
  })
  .strict();

/** What a planner saw: the message's kind and its suggestion as text (`""` for `cancel` and `expedite`). */
export const ExpectedActionMessage = ExpectedActionMessageFields.openapi("ExpectedActionMessage");

/**
 * The detail the MRP kernel stores with a message (`packages/scheduler/src/mrp/
 * messages.ts`), every key it writes named: `expedite` carries
 * `suggested_release_date` (today), the reschedules carry the due date, the
 * bucket and `delta_days`, and the quantity messages `suggested_qty` and
 * `delta_qty`. It was `.passthrough()` until 2026-10-19, which hid the expedite
 * key from the contract; the kernel's every message is now parsed against this
 * schema in `packages/scheduler` and `apps/api` unit tests (D-535 ruling 5).
 */
export const ActionMessageDetail = z
  .object({
    suggested_due_date: PlanningDate.optional(),
    suggested_bucket_start: PlanningMonday.optional(),
    suggested_release_date: PlanningDate.optional(),
    suggested_qty: PlanningQty.optional(),
    delta_days: z.number().int().optional(),
    delta_qty: z.string().optional(),
    reason_key: ExplanationKey,
  })
  .openapi("ActionMessageDetail");

export const ActionMessageDismissal = z
  .object({
    kind: ActionMessageKind,
    suggestion: z.string(),
    reason: z.string(),
    dismissed_by: Uuid,
    dismissed_at: Timestamp,
  })
  .openapi("ActionMessageDismissal");

// --- planned orders (§2.10, §5 rows 42 to 49) ----------------------------------------

export const PlannedOrder = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    po_no: z.string(),
    kind: PlannedOrderKind,
    status: PlannedOrderStatus,
    product_id: Uuid,
    qty: PlanningQty,
    uom_id: Uuid,
    bucket_start: PlanningMonday,
    due_date: PlanningDate,
    start_date: PlanningDate,
    release_date: PlanningDate,
    late: z.boolean(),
    source_facility_id: Uuid.nullable(),
    bom_id: Uuid.nullable(),
    routing_id: Uuid.nullable(),
    low_level_code: z.number().int().min(0).max(12),
    mps_line_id: Uuid.nullable(),
    mrp_run_id: Uuid,
    action_message: ActionMessageKind.nullable(),
    /** The message as accept and dismiss compare it; null with no message. */
    action_message_expected: ExpectedActionMessageFields.nullable(),
    action_message_detail: ActionMessageDetail.nullable(),
    action_message_dismissal: ActionMessageDismissal.nullable(),
    /** The explanation carries `exceeds_approved_mps` (§4.5's chip). */
    exceeds_mps: z.boolean(),
    /** Computed at read: the BOM or routing effective at `start_date` is not the pinned one (D-387 ruling 8). */
    stale_pin: z.boolean(),
    firmed_by: Uuid.nullable(),
    firmed_at: Timestamp.nullable(),
    cancelled_kind: PlannedOrderCancelledKind.nullable(),
    cancelled_reason: z.string().nullable(),
    cancelled_by: Uuid.nullable(),
    cancelled_at: Timestamp.nullable(),
    /** Set together on a converted order, null on every other (D-389 ruling 3). */
    converted_kind: PlannedOrderConvertedKind.nullable(),
    /** Module 04's draft work order or module 06's draft transfer. */
    converted_ref_id: Uuid.nullable(),
    converted_by: Uuid.nullable(),
    converted_at: Timestamp.nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("PlannedOrder");

/** `action_message`: `true`, `false` or one message kind (§5 row 42). */
export const PlannedOrderMessageFilter = z.union([z.enum(["true", "false"]), ActionMessageKind]);

export const PlannedOrderSort = z.enum(["due_date", "po_no"]);

export const PlannedOrderListQuery = z.object({
  facility_id: Uuid,
  kind: PlannedOrderKind.optional(),
  status: PlannedOrderStatus.optional(),
  action_message: PlannedOrderMessageFilter.optional(),
  /** Due-date window, both inclusive. */
  from: PlanningDate.optional(),
  to: PlanningDate.optional(),
  product_id: Uuid.optional(),
  planner_code: z.string().min(1).max(64).optional(),
  mrp_run_id: Uuid.optional(),
  /** The down walk: orders driven by this sales order line, through parents and transfer hops (D-387 ruling 7). */
  pegged_to_sales_order_line_id: Uuid.optional(),
  pegged_to_demand_entry_id: Uuid.optional(),
  sort: PlannedOrderSort.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

export const PlannedOrderListResponse = z
  .object({
    items: z.array(PlannedOrder),
    next_cursor: z.string().nullable(),
    /** True when the reader is served purchase orders only (§7 narrowing (c)). */
    purchase_only: z.boolean(),
  })
  .openapi("PlannedOrderListResponse");

const KindCounts = z.object({
  manufacture: z.number().int().min(0),
  purchase: z.number().int().min(0),
  transfer: z.number().int().min(0),
});

export const PlannedOrderSummary = z
  .object({
    facility_id: Uuid,
    by_status: z.object({
      proposed: KindCounts,
      firmed: KindCounts,
      converted: KindCounts,
      cancelled: KindCounts,
    }),
    action_messages: z.object({
      reschedule_in: z.number().int().min(0),
      reschedule_out: z.number().int().min(0),
      increase_qty: z.number().int().min(0),
      decrease_qty: z.number().int().min(0),
      cancel: z.number().int().min(0),
      expedite: z.number().int().min(0),
    }),
    purchase_only: z.boolean(),
  })
  .openapi("PlannedOrderSummary");

/**
 * A hop to an order or entry at a facility outside the reader's scope, or of a
 * kind the reader may not read, in exactly the seven fields of spec 03 §7
 * narrowing (b) (D-387 ruling 4).
 */
export const PegReducedNode = z
  .object({
    facility_id: Uuid,
    facility_name: z.string(),
    /** The order's number, or the entry's source kind for a pool entry. */
    po_no: z.string(),
    kind: z.string(),
    product_id: Uuid,
    product_sku: z.string().nullable(),
    qty: PlanningQty,
  })
  .strict()
  .openapi("PegReducedNode");

/** A planned order on the walk, in the shape its reader may see. */
export const PegOrderNode = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    po_no: z.string(),
    kind: PlannedOrderKind,
    status: PlannedOrderStatus,
    product_id: Uuid,
    qty: PlanningQty,
    due_date: PlanningDate,
  })
  .openapi("PegOrderNode");

/**
 * One edge of the pegging walk (§2.11), served flat in walk order with the
 * index of the edge it hangs from. Up, an edge is one of the order's pegs and
 * `order` the parent or destination order it names, whose own pegs follow at
 * the next depth; down, an edge is a peg of another order that names this one
 * and `order` is that order.
 */
export const PegEdge = z
  .object({
    /** The index in this list of the edge this one hangs from; null at depth 1. */
    parent: z.number().int().min(0).nullable(),
    depth: z.number().int().min(1).max(12),
    source_kind: PegSourceKind,
    source_ref_kind: PegRefKind,
    source_ref_id: Uuid.nullable(),
    demand_entry_id: Uuid.nullable(),
    qty: PlanningQty,
    /** Null when the peg belongs to an order the reader is served reduced (§7 narrowing (b)). */
    need_date: PlanningDate.nullable(),
    order: PegOrderNode.nullable(),
    reduced: PegReducedNode.nullable(),
  })
  .openapi("PegEdge");

/** A label the service renders for the reader (§7 narrowing (a)). */
export const ExplanationLabel = z
  .object({
    ref_kind: PegRefKind.exclude(["none"]),
    ref_id: Uuid,
    /** An order or plan number; null when the reader may not see it. */
    number: z.string().nullable(),
    /** A sales order line's label as module 09 renders it for the reader. */
    sales_order: z
      .object({
        order_id: Uuid.nullable(),
        order_no: z.string().nullable(),
        line_no: z.number().int().min(1).nullable(),
        customer_name: z.string().nullable(),
      })
      .nullable(),
    facility_id: Uuid.nullable(),
    product_id: Uuid.nullable(),
    /** True when the hop lies outside the reader's scope, so only the reduced shape is served. */
    reduced: z.boolean(),
  })
  .openapi("ExplanationLabel");

export const PlannedOrderDetail = z
  .object({
    order: PlannedOrder,
    explanation: PlannedOrderExplanation,
    labels: z.array(ExplanationLabel),
    pegs_up: z.array(PegEdge),
    pegs_down: z.array(PegEdge),
    /** Nodes the 12-level, 500-node bound left out, exactly (§5 row 44). */
    walk_hidden: z.number().int().min(0),
  })
  .openapi("PlannedOrderDetail");

export const PlannedOrderIdParam = z.object({ id: Uuid });

export const PLANNED_ORDER_BULK_MAX = 200;
export const PLANNED_ORDER_REASON_MAX = 500;

const Reason = z
  .string()
  .max(PLANNED_ORDER_REASON_MAX)
  .refine((v) => v.trim().length > 0, { message: "a reason of only spaces is no reason" });

export const PlannedOrderFirmRequest = z
  .object({ ids: z.array(Uuid).min(1).max(PLANNED_ORDER_BULK_MAX) })
  .strict()
  .openapi("PlannedOrderFirmRequest");

/** `POST /v1/planned-orders/convert` (§5 row 50, D-389 ruling 3): ids only. */
export const PlannedOrderConvertRequest = z
  .object({ ids: z.array(Uuid).min(1).max(PLANNED_ORDER_BULK_MAX) })
  .strict()
  .openapi("PlannedOrderConvertRequest");

export const PlannedOrderCancelRequest = z
  .object({ reason: Reason })
  .strict()
  .openapi("PlannedOrderCancelRequest");

export const ActionMessageAcceptRequest = z
  .object({ expected_message: ExpectedActionMessage })
  .strict()
  .openapi("ActionMessageAcceptRequest");

export const ActionMessageDismissRequest = z
  .object({ expected_message: ExpectedActionMessage, reason: Reason })
  .strict()
  .openapi("ActionMessageDismissRequest");

/** `POST /v1/planned-orders/action-messages/accept`: a JSON array (§5 row 49). */
export const ActionMessageBulkAcceptRequest = z
  .array(z.object({ id: Uuid, expected_message: ExpectedActionMessage }).strict())
  .min(1)
  .max(PLANNED_ORDER_BULK_MAX)
  .openapi("ActionMessageBulkAcceptRequest");

/** One id's refusal inside a bulk answer, in the problem's own status and code. */
export const PlannedOrderRefusal = z
  .object({
    status: z.number().int().min(400).max(599),
    code: z.string(),
    /** The order's current message, on 409 `ACTION_MESSAGE_CHANGED`. */
    current_message: ExpectedActionMessageFields.nullable().optional(),
    /** The order's status, on 409 `PLANNED_ORDER_NOT_PROPOSED` and `PLANNED_ORDER_NOT_FIRMED`. */
    order_status: PlannedOrderStatus.optional(),
  })
  .openapi("PlannedOrderRefusal");

export const PlannedOrderBulkResult = z
  .object({
    id: Uuid,
    /** `firmed` or `accepted`; null when the id was refused. */
    result: z.enum(["firmed", "accepted"]).nullable(),
    refusal: PlannedOrderRefusal.nullable(),
  })
  .openapi("PlannedOrderBulkResult");

export const PlannedOrderBulkResponse = z
  .object({ results: z.array(PlannedOrderBulkResult) })
  .openapi("PlannedOrderBulkResponse");

/**
 * One id's refusal inside a conversion answer (§5 row 50, §3.4): the problem's
 * own status and code, with the facts each code names.
 */
export const PlannedOrderConvertRefusal = PlannedOrderRefusal.extend({
  /** On 422 `PLANNED_ORDER_NO_ROUTING`: the product and the start date the routing was looked for at. */
  product_id: Uuid.optional(),
  start_date: PlanningDate.optional(),
  /** On 422 `PLANNED_ORDER_PIN_INVALID`: the pin refused and why. */
  bom_id: Uuid.optional(),
  routing_id: Uuid.optional(),
  reason: z.enum(["bom_archived", "routing_archived", "routing_without_steps"]).optional(),
}).openapi("PlannedOrderConvertRefusal");

export const PlannedOrderConvertResult = z
  .object({
    id: Uuid,
    /** `converted` when the id converted now or had converted before; null when refused. */
    status: z.literal("converted").nullable(),
    /** `ALREADY_CONVERTED` when the order had converted before; null otherwise. */
    code: z.literal("ALREADY_CONVERTED").nullable(),
    converted_kind: PlannedOrderConvertedKind.nullable(),
    converted_ref_id: Uuid.nullable(),
    refusal: PlannedOrderConvertRefusal.nullable(),
  })
  .openapi("PlannedOrderConvertResult");

export const PlannedOrderConvertResponse = z
  .object({ results: z.array(PlannedOrderConvertResult) })
  .openapi("PlannedOrderConvertResponse");

export const PlannedOrderStatusProblem = Problem.extend({
  order_status: PlannedOrderStatus.optional(),
}).openapi("PlannedOrderStatusProblem");

export const ActionMessageConflictProblem = Problem.extend({
  order_status: PlannedOrderStatus.optional(),
  current_message: ExpectedActionMessageFields.nullable().optional(),
}).openapi("ActionMessageConflictProblem");

// --- runs (§2.9, §5 rows 38 to 41) ---------------------------------------------------

const ListCount = z.number().int().min(0);
const ByKind = z.object({
  products_planned: ListCount,
  created: ListCount,
  updated: ListCount,
  cancelled: ListCount,
  action_messages: ListCount,
});
const FacilityProduct = z.object({ facility_id: Uuid, product_id: Uuid });

/**
 * `mrp_runs.stats` (§2.9). Every list holds at most 200 entries with its exact
 * count in `{list}_count` beside it (D-376 ruling 2). A queued, running or
 * failed run serves what it stored, so every field is optional; a procurement
 * reader is served the fields §7's table names, with `stats_narrowed: true`
 * on the run.
 */
export const MrpRunStats = z
  .object({
    products_planned: ListCount,
    planned_orders_created: ListCount,
    planned_orders_updated: ListCount,
    planned_orders_cancelled: ListCount,
    action_messages: ListCount,
    by_kind: z.object({ manufacture: ByKind, purchase: ByKind, transfer: ByKind }),
    max_low_level_code: z.number().int().min(0).max(12),
    elapsed_ms: ListCount,
    conflict_retries: ListCount,
    shutdown_requeues: ListCount,
    superseded_by: Uuid,
    optional_lines_skipped: ListCount,
    skipped: z.array(
      z.object({
        bom_line_id: Uuid,
        reason: z.enum(["UOM_NOT_CONVERTIBLE", "OUTPUT_UOM_NOT_CONVERTIBLE"]),
      }),
    ),
    skipped_count: ListCount,
    /**
     * Open work orders module 04 left out of the run's component demand
     * because their quantity no longer restates in their BOM's output unit or
     * their product's base unit (spec 03 §3.3e (b), D-407 ruling 3).
     */
    skipped_work_orders: z.array(
      z.object({
        work_order_id: Uuid,
        wo_no: z.string().min(1).max(64),
        reason: z.enum(["UOM_NOT_CONVERTIBLE", "MEASURE_PRECISION_LOSS"]),
      }),
    ),
    skipped_work_orders_count: ListCount,
    no_bom: z.array(
      z.object({
        product_id: Uuid,
        qty: PlanningQty,
        first_date: PlanningDate,
        mps_line_id: Uuid.optional(),
      }),
    ),
    no_bom_count: ListCount,
    supply_limited: z.array(
      z.object({ product_id: Uuid, qty: PlanningQty, first_date: PlanningDate }),
    ),
    supply_limited_count: ListCount,
    defaulted_parameters: z.array(
      z.object({
        product_id: Uuid,
        order_kind: z.enum(["manufacture", "purchase"]),
        lead_time_days: z.number().int().min(0),
        lead_time_source: z.enum(["product", "zero"]),
      }),
    ),
    defaulted_parameters_count: ListCount,
    defaulted_parameters_by_kind: z.object({ manufacture: ListCount, purchase: ListCount }),
    transfer_source_inactive: z.array(z.object({ product_id: Uuid, source_facility_id: Uuid })),
    transfer_source_inactive_count: ListCount,
    depth_exceeded: z.array(z.object({ product_ids: z.array(Uuid) })),
    depth_exceeded_count: ListCount,
    bom_cycle: z.array(z.object({ product_ids: z.array(Uuid) })),
    bom_cycle_count: ListCount,
    byproduct_cycle: z.array(z.object({ product_id: Uuid })),
    byproduct_cycle_count: ListCount,
    product_cycles: z.array(z.array(FacilityProduct)),
    product_cycles_count: ListCount,
    uncalendared_work_centers: z.array(z.object({ work_center_id: Uuid })),
    uncalendared_work_centers_count: ListCount,
    closed_work_centers: z.array(z.object({ work_center_id: Uuid })),
    closed_work_centers_count: ListCount,
    closed_facility_windows: z.array(
      z.object({ facility_id: Uuid, from: PlanningDate, to: PlanningDate }),
    ),
    closed_facility_windows_count: ListCount,
    mps_exceeded: z.array(
      z.object({ product_id: Uuid, bucket_start: PlanningMonday, excess: PlanningQty }),
    ),
    mps_exceeded_count: ListCount,
    mps_unplanned: z.array(z.object({ product_id: Uuid, reason: z.enum(["no_plan", "no_cell"]) })),
    mps_unplanned_count: ListCount,
    remnant_registry_mismatch: ListCount,
    orchestration: z.object({
      pass_no: z.number().int().min(1).max(3),
      fixpoint: z.enum(["converged", "not_reached"]).nullable(),
      /** Runs of the last pass that did not succeed; present only when some did not (D-395 ruling 63). */
      failed_runs: z.number().int().min(1).optional(),
      cyclic_products: z.array(FacilityProduct).optional(),
    }),
    /** Procurement's narrowing (§7): true when either graph list is not empty. */
    graph_excluded: z.boolean(),
  })
  .partial()
  .openapi("MrpRunStats", {
    description:
      "Empty while queued; a succeeded run carries every count. Lists hold at most 200 entries beside their exact count.",
  });

export const MrpRunPropagation = z
  .object({ origin_run_id: Uuid.optional(), depth: z.number().int().min(0) })
  .openapi("MrpRunPropagation");

export const MrpRun = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    run_no: z.string(),
    trigger: MrpRunTrigger,
    trigger_count: z.number().int().min(1),
    status: MrpRunStatus,
    mps_plan_id: Uuid.nullable(),
    demand_refresh_id: Uuid.nullable(),
    due_at: Timestamp,
    orchestration_id: Uuid.nullable(),
    pass_no: z.number().int().min(1).max(3).nullable(),
    propagation: MrpRunPropagation,
    params: z.object({ horizon_weeks: z.number().int().min(1).max(52).optional() }),
    stats: MrpRunStats,
    /** True when the reader is served §7's procurement subset of the stats. */
    stats_narrowed: z.boolean(),
    error: MrpRunError.nullable(),
    /** Keys and ids that explain `error`, never words; absent for a reader served §7's narrowed stats. */
    error_detail: z.record(z.string(), z.unknown()).nullable().optional(),
    started_at: Timestamp.nullable(),
    finished_at: Timestamp.nullable(),
    created_by: Uuid.nullable(),
    created_at: Timestamp,
  })
  .openapi("MrpRun");

export const MrpRunListQuery = z.object({
  facility_id: Uuid,
  status: MrpRunStatus.optional(),
  orchestration_id: Uuid.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

export const MrpRunListResponse = z
  .object({
    items: z.array(MrpRun),
    next_cursor: z.string().nullable(),
    /** Runs that ended failed since the facility's last success, merged rows left out (D-388 ruling 8). */
    consecutive_failures: z.number().int().min(0),
    /** The facility's last nightly MRP claim (`planning_settings.nightly_mrp_on`). */
    nightly_mrp_on: PlanningDate.nullable(),
  })
  .openapi("MrpRunListResponse");

export const MrpRunIdParam = z.object({ id: Uuid });

export const MrpRunRequest = z.object({ facility_id: Uuid }).strict().openapi("MrpRunRequest");

/** 202: the facility's queued run, joined or inserted (§5 row 38). */
export const MrpRunAccepted = z
  .object({ mrp_run_id: Uuid, run_no: z.string(), status: MrpRunStatus })
  .openapi("MrpRunAccepted");

export const MrpGroupPassRequest = z.object({}).strict().openapi("MrpGroupPassRequest");

/** 202: the manual pass's order and its stated skips (§5 row 39). */
export const MrpGroupPassAccepted = z
  .object({
    orchestration_id: Uuid,
    facilities: z.array(z.object({ facility_id: Uuid, position: z.number().int().min(1) })),
    skipped: z.array(
      z.object({ facility_id: Uuid, reason: z.literal("no_calendar_nothing_to_plan") }),
    ),
  })
  .openapi("MrpGroupPassAccepted");

export const MrpGroupPassConflictProblem = Problem.extend({
  orchestration_id: Uuid.optional(),
}).openapi("MrpGroupPassConflictProblem");

// --- audit actions (§5's P3-H audit rows) ---------------------------------------------

export const MRP_AUDIT_ACTIONS = {
  mrpRunRequested: "mrp_run.requested",
  plannedOrderFirmed: "planned_order.firmed",
  plannedOrderCancelled: "planned_order.cancelled",
  actionMessageAccepted: "planned_order.action_message_accepted",
  actionMessageDismissed: "planned_order.action_message_dismissed",
  plannedOrderConverted: "planned_order.converted",
} as const;
